// THE ASSET VOCABULARY REGISTRY — the register's domain nouns, inline and
// trilingual (the charter's vocabulary law): statuses · event moments · the
// condition grades · the derived book statuses. ONE home read by BOTH ends —
// the backend's import/export sheets and the frontend's i18n injection — so
// the pre-charter duplication (backend columns.ts labels beside three locale
// files) is structurally dead. Words move here by edit; screens re-render.
//
// VOCABULARY vs PROSE: nouns live here; sentences (toasts, hints, captions)
// stay in locale files. Plural-bearing member words live on the charter
// (MEMBER_WORDS) with the Intl.PluralRules helper.
export const ASSET_STATUS_LABELS = {
    deployed: { en: 'In service', fr: 'En service', ar: 'قيد الخدمة' },
    stocked: { en: 'In stock', fr: 'En stock', ar: 'في المخزون' },
    on_hold: { en: 'On hold', fr: 'En attente', ar: 'موقوف' },
    retired: { en: 'Retired', fr: 'Retiré du service', ar: 'مسحوب من الخدمة' },
    lost: { en: 'Lost', fr: 'Perdu', ar: 'مفقود' },
};
export const ASSET_EVENT_LABELS = {
    created: { en: 'Created', fr: 'Créé', ar: 'إنشاء' },
    moved: { en: 'Moved', fr: 'Déplacé', ar: 'نقل' },
    replaced: { en: 'Replaced', fr: 'Remplacé', ar: 'استبدال' },
    adjusted: { en: 'Count adjusted', fr: 'Nombre ajusté', ar: 'تعديل العدد' },
    put_on_hold: { en: 'Put on hold', fr: 'Mis en attente', ar: 'إيقاف' },
    returned_to_service: { en: 'Returned to service', fr: 'Remis en service', ar: 'إعادة إلى الخدمة' },
    retired: { en: 'Retired', fr: 'Retiré du service', ar: 'سحب من الخدمة' },
    reinstated: { en: 'Reinstated', fr: 'Réintégré', ar: 'إلغاء السحب' },
    lost: { en: 'Declared lost', fr: 'Déclaré perdu', ar: 'الإبلاغ عن فقدان' },
    recovered: { en: 'Recovered', fr: 'Retrouvé', ar: 'استرداد' },
    written_off: { en: 'Written off', fr: 'Radié', ar: 'شطب' },
    disposed: { en: 'Disposed', fr: 'Sortie définitive', ar: 'التخلص' },
    ownership_transferred: { en: 'Ownership transferred', fr: 'Propriété transférée', ar: 'نقل الملكية' },
};
export const ASSET_CONDITION_LABELS = {
    good: { en: 'Good', fr: 'Bon', ar: 'جيدة' },
    fair: { en: 'Fair', fr: 'Correct', ar: 'مقبولة' },
    poor: { en: 'Degraded', fr: 'Dégradé', ar: 'متدهورة' },
    failed: { en: 'Failed', fr: 'Défaillant', ar: 'معطلة' },
};
// The normalized reason vocabulary (asset_events.reason_type) — the filterable
// "why" every door offers; the import sheet's `reason` cell speaks these words.
export const ASSET_EVENT_REASON_LABELS = {
    failed: { en: 'Failed', fr: 'En panne', ar: 'عطل' },
    damaged: { en: 'Damaged', fr: 'Endommagé', ar: 'متضرر' },
    obsolete: { en: 'Obsolete', fr: 'Obsolète', ar: 'متقادم' },
    upgrade: { en: 'Upgrade', fr: 'Mise à niveau', ar: 'ترقية' },
    maintenance: { en: 'Maintenance', fr: 'Maintenance', ar: 'صيانة' },
    awaiting_spares: { en: 'Awaiting spares', fr: 'En attente de pièces', ar: 'بانتظار قطع الغيار' },
    standby: { en: 'Standby', fr: 'En réserve', ar: 'احتياطي' },
    theft_suspected: { en: 'Suspected theft', fr: 'Vol suspecté', ar: 'اشتباه سرقة' },
    not_found: { en: 'Not found', fr: 'Introuvable', ar: 'غير موجود' },
    found: { en: 'Found', fr: 'Retrouvé', ar: 'تم العثور عليه' },
    expansion: { en: 'Expansion', fr: 'Extension', ar: 'توسعة' },
    site_decommission: { en: 'Site decommission', fr: 'Démantèlement du site', ar: 'إخراج الموقع من الخدمة' },
    swap: { en: 'Swap', fr: 'Échange', ar: 'استبدال' },
    repaired: { en: 'Repaired', fr: 'Réparé', ar: 'أُصلح' },
    refurbished: { en: 'Refurbished', fr: 'Remis à neuf', ar: 'مُجدَّد' },
    inspection_passed: { en: 'Inspection passed', fr: 'Inspection réussie', ar: 'اجتاز الفحص' },
    beyond_repair: { en: 'Beyond repair', fr: 'Irréparable', ar: 'غير قابل للإصلاح' },
    stock_rebalance: { en: 'Stock rebalance', fr: 'Rééquilibrage de stock', ar: 'موازنة مخزون' },
    count_correction: { en: 'Count correction', fr: 'Correction d’inventaire', ar: 'تصحيح جرد' },
    suspected_fault: { en: 'Suspected fault', fr: 'Défaut suspecté', ar: 'اشتباه عطل' },
    recorded_in_error: { en: 'Recorded in error', fr: 'Saisi par erreur', ar: 'سُجّل بالخطأ' },
    date_correction: { en: 'Date correction', fr: 'Correction de date', ar: 'تصحيح التاريخ' },
    other: { en: 'Other', fr: 'Autre', ar: 'أخرى' },
};
// The import sheet's location-kind words (the doors' tiles — one vocabulary
// for the `location_type` cell and the legend).
export const ASSET_LOCATION_TYPE_LABELS = {
    site: { en: 'Site', fr: 'Site', ar: 'موقع' },
    site_storage: { en: 'Site storage', fr: 'Stockage du site', ar: 'مخزن الموقع' },
    depot: { en: 'Depot', fr: 'Dépôt', ar: 'مستودع' },
    vehicle: { en: 'Vehicle stock', fr: 'Stock véhicule', ar: 'مخزون المركبة' },
};
export const BOOK_STATUS_LABELS = {
    on_books: { en: 'On the books', fr: 'Au bilan', ar: 'في الدفاتر' },
    written_off: { en: 'Written off', fr: 'Amorti', ar: 'مشطوب' },
    disposed: { en: 'Disposed', fr: 'Sorti', ar: 'مُتصرَّف به' },
};
